(function () {
  'use strict';

  if (!window.algoliaConfig) {
    console.warn('Author Blogs: Algolia config not found');
    return;
  }

  const { appId, searchKey, indexName } = window.algoliaConfig;

  if (!appId || !searchKey || !indexName) {
    console.warn('Author Blogs: Algolia credentials incomplete');
    return;
  }

  // Shared attribute list — matches algolia-search.js
  const SEARCH_ATTRIBUTES = [
    'objectID', 'title', 'url', 'handle',
    'image', 'featured_image', 'published_at',
    'created_at', 'author', 'author.name', 'blog_handle', 'blog',
    'excerpt', 'body_html_safe', 'content'
  ];

  let algoliaIndex = null;
  let currentPage = 0;
  let totalPages = 0;
  let resultsPerPage = 24;
  let authorHandle = '';
  let authorName = '';

  function initAuthorBlogs() {
    const component = document.querySelector('[data-author-blogs-algolia]');
    if (!component) return;

    if (typeof algoliasearch === 'undefined') {
      setTimeout(initAuthorBlogs, 100);
      return;
    }

    const client = algoliasearch(appId, searchKey);
    algoliaIndex = client.initIndex(indexName);

    // Get configuration from data attributes
    resultsPerPage = parseInt(component.dataset.resultsPerPage, 10) || 24;
    authorHandle = component.dataset.authorHandle || '';
    authorName = component.dataset.authorName || '';

    if (!authorHandle && !authorName) {
      console.warn('Author Blogs: No author handle or name provided');
      return;
    }

    // Parse URL for initial state
    parseURLState();

    // Perform initial search
    performSearch();

    // Handle browser navigation
    window.addEventListener('popstate', () => {
      parseURLState();
      performSearch();
    });

    console.log('Author Blogs Algolia initialized for author:', authorName || authorHandle);
  }


  function parseURLState() {
    const params = new URLSearchParams(window.location.search);
    currentPage = Math.max(0, parseInt(params.get('page') || '1', 10) - 1);
  }

  function updateURL() {
    const params = new URLSearchParams();

    if (currentPage > 0) {
      params.set('page', currentPage + 1);
    }

    const newURL = params.toString()
      ? `${window.location.pathname}?${params.toString()}`
      : window.location.pathname;

    window.history.pushState({}, '', newURL);
  }


  async function performSearch() {
    const gridContainer = document.getElementById('author-blogs-grid');
    if (!gridContainer || !algoliaIndex) return;

    gridContainer.classList.add('loading');

    try {
      console.log('[Author Blogs] Searching with author.name facet filter:', authorName, '| page:', currentPage + 1);

      // Use Algolia search with facetFilters on author.name
      // This filters articles to only those where author.name matches the current author
      const searchParams = {
        hitsPerPage: resultsPerPage,
        page: currentPage,
        attributesToRetrieve: SEARCH_ATTRIBUTES
      };
      if (authorName) {
        searchParams.facetFilters = [`author.name:${authorName}`];
      }
      const response = await algoliaIndex.search('', searchParams);

      totalPages = response.nbPages;

      console.log('[Author Blogs] Results:', response.nbHits, 'total |', response.hits.length, 'on page', currentPage + 1, 'of', totalPages);

      renderResults(response.hits, response.nbHits);
      renderPagination();
      updateURL();

    } catch (error) {
      console.error('[Author Blogs] Search error:', error);
      gridContainer.innerHTML = `<p class="all-news-error">Error loading articles: ${error.message}</p>`;
    } finally {
      gridContainer.classList.remove('loading');
    }
  }


  function renderResults(hits, totalArticles) {
    const gridContainer = document.getElementById('author-blogs-grid');
    if (!gridContainer) return;

    if (hits.length === 0) {
      gridContainer.innerHTML = `
        <div class="all-news-no-results">
          <p>No articles found for this author.</p>
          <p>Please check back later for new content.</p>
        </div>
      `;
      return;
    }

    const cardsHtml = hits.map(hit => renderArticleCard(hit)).join('');
    gridContainer.innerHTML = cardsHtml;
  }

  // Helper to get blog handle from hit (handles different data structures)
  function getBlogHandle(hit) {
    if (hit.blog_handle) return hit.blog_handle;
    if (hit.blog && hit.blog.handle) return hit.blog.handle;
    if (hit.blog && typeof hit.blog === 'string') return hit.blog;
    return '';
  }

  function renderArticleCard(hit) {
    const imageUrl = hit.image || hit.featured_image || '';
    const title = hit.title || '';
    const blogHandle = getBlogHandle(hit);
    const url = hit.url || (blogHandle && hit.handle ? `/blogs/${blogHandle}/${hit.handle}` : '#');

    // Get author name - check author.name first, then author object, then fallback
    let displayAuthorName = '';
    if (hit.author) {
      if (typeof hit.author === 'object') {
        displayAuthorName = hit.author.name || hit.author.author_name || '';
      } else if (typeof hit.author === 'string') {
        displayAuthorName = hit.author;
      }
    }
    // Use the current author name as fallback
    if (!displayAuthorName && authorName) {
      displayAuthorName = authorName;
    }

    let publishDate = '';
    if (hit.published_at || hit.created_at) {
      const dateObj = new Date(hit.published_at || hit.created_at);
      publishDate = dateObj.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    }

    const blogTitle = hit.blog?.title || formatBlogHandle(blogHandle) || '';

    // Get excerpt - try multiple possible fields
    let excerpt = hit.excerpt || hit.body_html_safe || hit.content || '';
    // Strip HTML tags and limit length
    if (excerpt) {
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = excerpt;
      excerpt = tempDiv.textContent || tempDiv.innerText || '';
      // Limit to ~150 characters
      if (excerpt.length > 150) {
        excerpt = excerpt.substring(0, 150).trim() + '...';
      }
    }

    return `
      <article class="card card--article card--style-no_padding card--text-display--title_and_excerpt">
        <a class="card__image" href="${url}">
          <figure class="aspect-ratio--landscape aspect-ratio__mobile--widescreen">
            ${imageUrl ? `<img src="${imageUrl}" alt="${escapeHtml(title)}" class="img-absolute" loading="lazy" width="800" height="450">` : ''}
          </figure>
        </a>
        <a href="${url}" class="card__content">
          <div>
            <p class="card__blog-title text-xs">
              ${blogTitle ? `${escapeHtml(blogTitle)}` : ''}
              ${displayAuthorName ? ` &ndash; by ${escapeHtml(displayAuthorName)}` : ''}
              ${publishDate ? ` &ndash; ${publishDate}` : ''}
            </p>
            <p class="card__title">${escapeHtml(title)}</p>
            ${excerpt ? `<p class="card__description text-md">${escapeHtml(excerpt)}</p>` : ''}
          </div>
          <div class="card__bottom">
            <p class="button button--primary">${window.searchStrings?.read_more || 'Read More'}</p>
          </div>
        </a>
      </article>
    `;
  }

  function renderPagination() {
    const paginationContainer = document.getElementById('author-blogs-pagination');
    if (!paginationContainer) return;

    if (totalPages <= 1) {
      paginationContainer.innerHTML = '';
      return;
    }

    let html = '<nav class="pagination" role="navigation" aria-label="Pagination">';
    html += '<ul role="list">';

    // Previous
    if (currentPage > 0) {
      html += `
        <li class="prev">
          <a href="#" data-page="${currentPage - 1}" aria-label="Previous page">
            &larr; <span class="hide-mobile">Previous</span>
          </a>
        </li>
      `;
    }

    // Page numbers (show max 5)
    const maxVisible = 5;
    let startPage = Math.max(0, currentPage - Math.floor(maxVisible / 2));
    let endPage = Math.min(totalPages - 1, startPage + maxVisible - 1);

    if (endPage - startPage < maxVisible - 1) {
      startPage = Math.max(0, endPage - maxVisible + 1);
    }

    if (startPage > 0) {
      html += `<li><a href="#" data-page="0">1</a></li>`;
      if (startPage > 1) html += '<li><span>...</span></li>';
    }

    for (let i = startPage; i <= endPage; i++) {
      if (i === currentPage) {
        html += `<li><span class="pagination__item--current" aria-current="page">${i + 1}</span></li>`;
      } else {
        html += `<li><a href="#" data-page="${i}">${i + 1}</a></li>`;
      }
    }

    if (endPage < totalPages - 1) {
      if (endPage < totalPages - 2) html += '<li><span>...</span></li>';
      html += `<li><a href="#" data-page="${totalPages - 1}">${totalPages}</a></li>`;
    }

    // Next
    if (currentPage < totalPages - 1) {
      html += `
        <li class="next">
          <a href="#" data-page="${currentPage + 1}" aria-label="Next page">
            <span class="hide-mobile">Next</span> &rarr;
          </a>
        </li>
      `;
    }

    html += '</ul></nav>';
    paginationContainer.innerHTML = html;

    // Attach pagination listeners
    paginationContainer.querySelectorAll('a[data-page]').forEach(link => {
      link.addEventListener('click', (e) => {
        e.preventDefault();
        currentPage = parseInt(link.dataset.page, 10);
        performSearch();
        document.getElementById('author-blogs-grid')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    });
  }


  function escapeHtml(text) {
    if (!text) return '';
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  function formatBlogHandle(handle) {
    if (!handle) return '';
    return handle
      .replace(/-/g, ' ')
      .replace(/\b\w/g, c => c.toUpperCase());
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAuthorBlogs);
  } else {
    initAuthorBlogs();
  }
})();
